/**
 * Measure distance — spec §F5.10: "measure (crosshair; click vertices → red dashed
 * polyline, permanent km tooltip (haversine sum); re-click tool clears)".
 *
 * Style is the mockup's `measureClick`: `#B03A2E`, weight 2.5, `dashArray '6,4'`, a
 * permanent `.unit-tip` tooltip carrying the running §G7 haversine sum.
 *
 * Renders nothing — it is a Leaflet layer driven by an effect. `active` is owned by
 * `MapTools` (the tool column's toggle state); `onExit` is how Esc gives the column its
 * button back.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { polylineKm } from '../../lib/geo';
import { fmtNum } from '../../lib/format';
import { toast } from '../../lib/toast';
import { useMap } from './mapContext';
import { setToolArmed } from './panes';

export interface MeasureProps {
  active: boolean;
  /** Esc — cancel the tool entirely (clears *and* pops the toolbar button out). */
  onExit: () => void;
}

/** Mockup `measureClick`: red dashed, 2.5px. `interactive: false` per `AOI_STYLE`. */
export const MEASURE_STYLE = {
  color: '#B03A2E',
  weight: 2.5,
  dashArray: '6,4',
  interactive: false,
} as const;

export function Measure({ active, onExit }: MeasureProps) {
  const { t } = useTranslation();
  const map = useMap();
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  useEffect(() => {
    if (!active) return;

    let line: L.Polyline | null = null;
    const pts: L.LatLng[] = [];

    const redraw = () => {
      if (!pts.length) return;
      if (!line) {
        line = L.polyline(pts, { ...MEASURE_STYLE }).addTo(map);
        // Bound **once**: `bindTooltip` on an already-tooltipped layer leaves the previous
        // permanent tooltip on the map often enough to double-print the distance. The
        // content is then pushed, which is what a running total wants anyway.
        line.bindTooltip('', { permanent: true, className: 'unit-tip' });
      } else {
        line.setLatLngs(pts);
      }
      line.setTooltipContent(`${fmtNum(polylineKm(pts), 1)} km`);
      line.openTooltip();
    };

    const clear = () => {
      if (line) map.removeLayer(line);
      line = null;
      pts.length = 0;
    };

    const onClick = (e: L.LeafletMouseEvent) => {
      pts.push(e.latlng);
      redraw();
    };
    // Not in §F5.10, which specifies only "re-click tool clears": a double-click starts a
    // fresh measurement *without* leaving the tool, so taking a second distance does not
    // cost a round-trip to the toolbar (NOTES #313).
    // The two `click`s Leaflet fires first have already been pushed; `clear()` drops them.
    const onDblClick = () => clear();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitRef.current();
    };

    const container = map.getContainer();
    container.style.cursor = 'crosshair';
    // Otherwise the double-click that ends a measurement also zooms the map under it.
    map.doubleClickZoom.disable();
    // A click is a vertex, not a unit selection, while the crosshair is armed.
    setToolArmed(map, true);
    map.on('click', onClick);
    map.on('dblclick', onDblClick);
    window.addEventListener('keydown', onKey);
    toast(t('tools.measureHint'));

    return () => {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      window.removeEventListener('keydown', onKey);
      map.doubleClickZoom.enable();
      setToolArmed(map, false);
      container.style.cursor = '';
      clear();
    };
  }, [active, map, t]);

  return null;
}

export default Measure;
